import React from "react";

const SoundOverrideContext = React.createContext(null);

export const SoundOverrideProvider = ({ initial = false, children }) => {
  const [override, setOverride] = React.useState(initial);
  const timerRef = React.useRef(null);

  const clearTimer = React.useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const hardOverride = React.useCallback(() => {
    setOverride(true);
  }, []);

  const tempOverride = React.useCallback(
    (ms = 1 * 60 * 1000) => {
      setOverride(true);
      clearTimer();
      timerRef.current = setTimeout(() => {
        setOverride(false);
        timerRef.current = null;
      }, ms);
    },
    [clearTimer]
  );
  const hardOverrideClear = React.useCallback(() => {
    setOverride(false);
  }, []);
  const tempOverrideClear = React.useCallback(() => {
    clearTimer();
    setOverride(false);
  }, [clearTimer]);

  const toggle = React.useCallback(() => {
    setOverride((curr) => {
      const next = !curr;

      if (next) {
        clearTimer();
        timerRef.current = setTimeout(() => {
          setOverride(false);
          timerRef.current = null;
        }, 1 * 60 * 1000);
      } else {
        clearTimer();
      }
      return next;
    });
  }, [clearTimer]);

  React.useEffect(() => clearTimer, [clearTimer]);

  const value = React.useMemo(
    () => ({
      override,
      tempOverride,
      hardOverride,
      hardOverrideClear,
      tempOverrideClear,
      toggle,
    }),
    [
      override,
      tempOverride,
      tempOverrideClear,
      toggle,
      hardOverride,
      hardOverrideClear,
    ]
  );

  return (
    <SoundOverrideContext.Provider value={value}>
      {children}
    </SoundOverrideContext.Provider>
  );
};

export const useSoundOverride = () => {
  const context = React.useContext(SoundOverrideContext);
  if (!context) {
    throw new Error(
      "useSoundOverride must be used within a SoundOverrideProvider"
    );
  }
  return context;
};
